import React, { useContext, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FlowScreenWrapper } from "./FlowScreenWrapper";
import { useAuthContext } from "../../../AuthContextWrapper";
import { RadioAndResponseForm } from "./RadioAndResponseForm";
import { AssetRealEstate } from "../../../models/AssetDetail";
import { estateApi } from "../../../apis";
import { AssetContext } from "../../../AssetContextWrapper";
import { Estate } from "../../../models/Estate";

export enum Outcomes {
  yesIndividually = "Yes, owned individually",
  yesJointly = "Yes, owned jointly (with someone else)",
  noRented = "No, rented", // No, [Elrond] was renting
  notSure = "I’m not sure",
  skip = "Skip",
}

export const PropertyOwnershipScreen = (): JSX.Element => {
  const navigate = useNavigate();
  const { assetsLoaded } = useContext(AssetContext);
  const { flow, activeEstate, setActiveEstate } = useAuthContext()
  const { screenName } = useParams();
  const currentScreen = flow?.getCurrentFlowScreen(screenName);
  const primaryRealEstate = activeEstate?.primaryRealEstate;

  useEffect(() => {
    if (assetsLoaded && !activeEstate?.primaryRealEstate) {
      currentScreen &&
        flow?.navigateToNext(currentScreen, Outcomes.skip, navigate, {
          replace: true,
        });
    }
  }, [assetsLoaded]);

  const handleComplete = async (ownership: string): Promise<boolean> => {
    if (!currentScreen) throw Error("couldn't determine currentScreen");

    if (activeEstate && activeEstate?.primaryRealEstate?.detail) {
      (activeEstate.primaryRealEstate.detail as AssetRealEstate).ownership =
        ownership;
      // if rental, set the asset isProbate to false
      //  someday we'll know to exclude this from estate value calculations and/or create the report differently
      if (ownership === Outcomes.noRented) {
        activeEstate.primaryRealEstate.isProbate = false;
        if (flow?.flowPath === "on-boarding") {
          // since this home is a rental, we don't need have them go through the primary home flow
          activeEstate.primaryHomeComplete = true;
        }
      } else if (
        ownership === Outcomes.yesIndividually ||
        ownership === Outcomes.yesJointly
      ) {
        activeEstate.primaryRealEstate.isProbate = true;
      }
      try {
        await estateApi.saveAsset(primaryRealEstate);

        // support legacy has_real_estate recording
        const estate = activeEstate || new Estate();
        estate.hasRealEstate = ownership?.toLowerCase()?.includes("yes");
        await estate.saveEstate();
        setActiveEstate(estate);
      } catch (error) {
        throw new Error("could not save data");
      }
    }

    flow?.navigateToNext(currentScreen, ownership, navigate);
    return true;
  };

  return currentScreen && assetsLoaded ? (
    <FlowScreenWrapper
      title={`${activeEstate?.hasPassed ? "Did" : "Does"} ${
        activeEstate?.deceasedFirstName
      } own this property?`}
      subTitle="The primary home can be one of the most valuable assets in an estate. Its fair market value and remaining mortgage, if there was one, are often the deciding factors between what kind of Probate the estate will require."
    >
      <RadioAndResponseForm
        initAnswer={(primaryRealEstate?.detail as AssetRealEstate)?.ownership}
        answerLabel="Property ownership"
        errorText={`We need to know whether or not ${activeEstate?.deceasedFirstName} owned this property.`}
        options={[
          { value: Outcomes.yesIndividually },
          { value: Outcomes.yesJointly },
          {
            value: Outcomes.noRented,
            label: `No, ${activeEstate?.deceasedFirstName} rented`,
          },
          { value: Outcomes.notSure },
        ]}
        onComplete={handleComplete}
        onBack={() => navigate(-1)}
      />
    </FlowScreenWrapper>
  ) : (
    <></>
  );
};
